import React from 'react';

import {
  eligibleForHKEXProfile,
  stockHasMarketSMA,
  stockIsHKIndex,
  stockIsIndex,
  stockIsUSIndex,
} from '../../helpers/stockCode.helper';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { useUser } from '../../hooks/users';
import { StockLookupEntity } from '../../py-generated/datalouder/stock/types';
import AAIISwitch from './priceViewerSettings/AAIISwitch';
import ATRSwitch from './priceViewerSettings/ATRSwitch';
import BBSwitchButtons from './priceViewerSettings/BBSwitch';
import ChartPeriodSwitch from './priceViewerSettings/ChartPeriodSwitch';
import ChartUnitSwitch from './priceViewerSettings/ChartUnitSwitch';
import ConcentrationSwitch from './priceViewerSettings/ConcentrationSwitch';
import FearGreedSwitch from './priceViewerSettings/FearGreedSwitch';
import HSISwitch from './priceViewerSettings/HSISwitch';
import IndexSMASwitch from './priceViewerSettings/IndexSMASwitch';
import InstitutionalTradeSwitch from './priceViewerSettings/InstitutionalTradeSwitch';
import MACDSwitchButtons from './priceViewerSettings/MACDSwitch';
import MarketSMASwitch from './priceViewerSettings/MarketSMASwitch';
import MASwitchButtons from './priceViewerSettings/MASwitch';
import NOPStrategySwitch from './priceViewerSettings/NOPStrategySwitch';
import NOPSwitch from './priceViewerSettings/NOPSwitch';
import PriceViewerStockCodeSearchField from './priceViewerSettings/PriceViewerStockCodeSearchField';
import RetailTradeSwitch from './priceViewerSettings/RetailTradeSwitch';
import RSISwitch from './priceViewerSettings/RSISwitch';
import SouthboundSwitch from './priceViewerSettings/SouthboundSwitch';
import TurnoverOverlapSwitch from './priceViewerSettings/TurnoverOverlapSwitch';
import VolumeOverlapSwitch from './priceViewerSettings/VolumeOverlapSwitch';

interface Props {
  wrapAnalytics: (dataKey: string, callback: () => void) => () => void;
  stock: StockLookupEntity;
  region: string;
  editingStockCode: string;
  handleEditingStockCode: (value: string) => void;
  handleSearchInputKeyPressed: React.KeyboardEventHandler<HTMLInputElement>;
  handleSearchClick: () => void;
  stockCode: string;
  toggleShowParticipantNumber: () => void;
  noLittleSearchField: boolean;
  isSouthboundStock: boolean;
  showStrategy: boolean;
  validStrategyList: string[];
}

const PriceViewerPreferences: React.FC<Props> = ({
  wrapAnalytics,
  stock,
  region,
  editingStockCode,
  handleEditingStockCode,
  handleSearchInputKeyPressed,
  handleSearchClick,
  stockCode,
  toggleShowParticipantNumber,
  noLittleSearchField,
  isSouthboundStock,
  showStrategy,
  validStrategyList,
}) => {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const availableFeatures = useFeatureFlags();
  const user = useUser();
  const isAdmin = user && user.isAdmin;
  const enablePd = isAdmin || user?.id === 2795;
  return (
    <div
      className="dl-switch-container price-viewer-preference"
      style={{
        position: 'relative',
        zIndex: 7,
        padding: isMobile ? undefined : '0 5%',
      }}
    >
      <div>
        {!noLittleSearchField && (
          <PriceViewerStockCodeSearchField
            stock={stock}
            region={region}
            editingStockCode={editingStockCode}
            handleEditingStockCode={handleEditingStockCode}
            handleSearchInputKeyPressed={handleSearchInputKeyPressed}
            handleSearchClick={handleSearchClick}
          />
        )}
        <ChartPeriodSwitch wrapAnalytics={wrapAnalytics} />
        <ChartUnitSwitch wrapAnalytics={wrapAnalytics} stockCode={stockCode} />
        {!stockIsHKIndex(stockCode) && isAdmin && (
          <VolumeOverlapSwitch wrapAnalytics={wrapAnalytics} />
        )}
        {!stockIsUSIndex(stockCode) && isAdmin && (
          <TurnoverOverlapSwitch wrapAnalytics={wrapAnalytics} />
        )}
        {!isAdmin && <VolumeOverlapSwitch wrapAnalytics={wrapAnalytics} />}
        <BBSwitchButtons wrapAnalytics={wrapAnalytics} />
        <MACDSwitchButtons wrapAnalytics={wrapAnalytics} />
        <MASwitchButtons wrapAnalytics={wrapAnalytics} />
        <RSISwitch wrapAnalytics={wrapAnalytics} />
        <ATRSwitch wrapAnalytics={wrapAnalytics} />
      </div>
      <div style={{ marginTop: isMobile ? 10 : 3 }}>
        {region === 'hk' && eligibleForHKEXProfile(stockCode) && (
          <>
            <NOPSwitch
              wrapAnalytics={wrapAnalytics}
              toggleShowParticipantNumber={toggleShowParticipantNumber}
            />
            {isSouthboundStock && (
              <SouthboundSwitch wrapAnalytics={wrapAnalytics} />
            )}
            <ConcentrationSwitch wrapAnalytics={wrapAnalytics} />
            <RetailTradeSwitch wrapAnalytics={wrapAnalytics} />
            <InstitutionalTradeSwitch wrapAnalytics={wrapAnalytics} />
          </>
        )}

        {stockHasMarketSMA(stockCode) && (
          <>
            <MarketSMASwitch wrapAnalytics={wrapAnalytics} />
          </>
        )}
        {stockIsIndex(stockCode) && (
          <>
            <IndexSMASwitch wrapAnalytics={wrapAnalytics} />
          </>
        )}
        {availableFeatures.includes('PRICE_VIEWER_MARKET_BREADTH') &&
          stockCode === 'BTCUSDT' && (
            <MarketSMASwitch wrapAnalytics={wrapAnalytics} />
          )}
        {stockIsUSIndex(stockCode) && (
          <FearGreedSwitch wrapAnalytics={wrapAnalytics} />
        )}
        {stockIsUSIndex(stockCode) && (
          <AAIISwitch wrapAnalytics={wrapAnalytics} />
        )}
        {enablePd && stockCode === '^HSI' && (
          <HSISwitch wrapAnalytics={wrapAnalytics} />
        )}
        {availableFeatures.includes('PRICE_VIEWER_NOP_STRATEGY') &&
          showStrategy &&
          region === 'hk' &&
          eligibleForHKEXProfile(stockCode) && (
            <NOPStrategySwitch
              wrapAnalytics={wrapAnalytics}
              validStrategyList={validStrategyList}
            />
          )}
      </div>
    </div>
  );
};

export default PriceViewerPreferences;
